import { useEffect, useState } from "react";
import {
  EVENTO_ABRIR_PREFERENCIAS,
  aplicarConsentimento,
  lerConsentimento,
  salvarConsentimento,
} from "@/lib/consentimento";

// Banner de cookies (LGPD). Recusar tem o mesmo destaque de aceitar e a
// escolha pode ser revista a qualquer momento pelo rodapé.
export default function CookieConsent() {
  const [aberto, setAberto] = useState(false);
  const [personalizar, setPersonalizar] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const salvo = lerConsentimento();
    if (salvo) {
      // garante que pixels carregados depois recebam a escolha
      aplicarConsentimento(salvo);
      setAnalytics(salvo.analytics);
      setMarketing(salvo.marketing);
    } else {
      setAberto(true);
    }

    const abrir = () => {
      const atual = lerConsentimento();
      setAnalytics(Boolean(atual?.analytics));
      setMarketing(Boolean(atual?.marketing));
      setPersonalizar(true);
      setAberto(true);
    };
    window.addEventListener(EVENTO_ABRIR_PREFERENCIAS, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR_PREFERENCIAS, abrir);
  }, []);

  const escolher = (escolha: { analytics: boolean; marketing: boolean }) => {
    salvarConsentimento(escolha);
    setAberto(false);
    setPersonalizar(false);
  };

  if (!aberto) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Preferências de cookies"
      className="fixed inset-x-0 bottom-0 z-[60] p-3 md:p-5 print:hidden"
    >
      <div className="mx-auto max-w-4xl rounded-lg bg-white p-5 text-gray-700 shadow-2xl ring-1 ring-gray-200">
        <p className="font-bold text-gray-900">Sua privacidade</p>
        <p className="mt-2 text-sm">
          Usamos cookies necessários para o site funcionar (login, carrinho e
          segurança). Com a sua permissão, também usamos cookies de estatística
          para entender como o site é usado e de marketing para mostrar anúncios
          de eventos. Saiba mais na{" "}
          <a href="/auth/politica" target="_blank" className="font-semibold text-myblue underline">
            Política de Privacidade
          </a>
          .
        </p>

        {personalizar && (
          <div className="mt-4 space-y-3 border-t pt-4 text-sm">
            <Opcao
              titulo="Necessários"
              descricao="Login, carrinho, pagamento e proteção contra fraude. Não podem ser desativados."
              ativo
              bloqueado
            />
            <Opcao
              titulo="Estatística"
              descricao="Google Analytics: páginas visitadas e etapas da compra, para melhorar o site."
              ativo={analytics}
              onChange={setAnalytics}
            />
            <Opcao
              titulo="Marketing"
              descricao="Pixel do Facebook e pixels de anúncio dos organizadores dos eventos."
              ativo={marketing}
              onChange={setMarketing}
            />
          </div>
        )}

        <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {personalizar ? (
            <button
              type="button"
              onClick={() => escolher({ analytics, marketing })}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold hover:bg-gray-50"
            >
              Salvar preferências
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setPersonalizar(true)}
              className="rounded-md px-4 py-2 text-sm font-semibold text-myblue hover:underline"
            >
              Personalizar
            </button>
          )}
          <button
            type="button"
            onClick={() => escolher({ analytics: false, marketing: false })}
            className="rounded-md bg-myblue px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            Recusar
          </button>
          <button
            type="button"
            onClick={() => escolher({ analytics: true, marketing: true })}
            className="rounded-md bg-myblue px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
          >
            Aceitar todos
          </button>
        </div>
      </div>
    </div>
  );
}

function Opcao({
  titulo,
  descricao,
  ativo,
  bloqueado,
  onChange,
}: {
  titulo: string;
  descricao: string;
  ativo: boolean;
  bloqueado?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label className={`flex items-start justify-between gap-4 ${bloqueado ? "" : "cursor-pointer"}`}>
      <span>
        <span className="block font-semibold text-gray-900">{titulo}</span>
        <span className="block text-gray-500">{descricao}</span>
      </span>
      <input
        type="checkbox"
        className="mt-1 h-5 w-5 shrink-0 rounded text-myblue focus:ring-myblue disabled:opacity-60"
        checked={ativo}
        disabled={bloqueado}
        onChange={(e) => onChange?.(e.target.checked)}
      />
    </label>
  );
}
